import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Pane,
  Card,
  Grid,
  Row,
  Col,
  Button,
  PopoutBody,
  Popout
} from '@sm/wds-react';
import {
  t,
  T,
  defineMessages,
  FormattedDateTime,
  FormattedRelativeTime,
  FormattedNumber
} from '@sm/intl';

const MSG = defineMessages({
  DATE_A: {
    id: 'IntlKitchenSink.dateA',
    defaultMessage: 'Expiration date: {expirationDate, date, short}',
    description: '[Type: Label][Vis.: low] - This is a short date.'
  },
  DATE_B: {
    id: 'IntlKitchenSink.dateB',
    defaultMessage: 'Due date: {dueDate, date, long}',
    description: '[Type: Label][Vis.: med] - This is a long date.'
  },
  DATE_C: {
    id: 'IntlKitchenSink.dateC',
    defaultMessage: 'Created: {formattedDate}',
    description: '[Type: Label][Vis.: med] - This is a long date.'
  },
  TIME_A: {
    id: 'IntlKitchenSink.timeA',
    defaultMessage: 'Time: {expiryTime, time, short}',
    description: '[Type: Label][Vis.: med] - This is a long time.'
  },
  TIME_B: {
    id: 'IntlKitchenSink.timeB',
    defaultMessage: 'Time: {expiryTime, time, long}',
    description: '[Type: Label][Vis.: med] - This is a short time.'
  },
  HTML_A: {
    id: 'IntlKitchenSink.htmlA',
    defaultMessage:
      'This message <strong>contains some html</strong> but this is <em>normal</em>.',
    description:
      '[Type: Paragraph][Vis.: med] - The goal of this is to test translation of html content.'
  },
  MESSAGE_A: {
    id: 'IntlKitchenSink.messageA',
    defaultMessage: 'Hello everyone',
    description: '[Type: Label][Vis.: med] - simple string'
  },
  MESSAGE_B: {
    id: 'IntlKitchenSink.messageB',
    defaultMessage: 'Package Type: {packageName}',
    description:
      '[Type: Label][Vis.: med] - Tells the user which package they purchased.'
  },
  MESSAGE_C: {
    id: 'IntlKitchenSink.messageC',
    defaultMessage:
      'The following text should be bold: {firstName}, and this one should be italic: {lastName}',
    description:
      "[Type: Paragraph][Vis.: med] - Showing the user's username in bold"
  },
  MULTI_A: {
    id: 'IntlKitchenSink.multiA',
    defaultMessage:
      '{gender, select, male { He } female { She } other { They }} will respond shortly.',
    description:
      '[Type: label][Vis.: med] - Testing select in ICU format. Uses gender to respond correctly to the user.'
  },
  MULTI_B: {
    id: 'IntlKitchenSink.multiB',
    defaultMessage:
      'You have {num, plural, =0 {no surveys.} =1 {one survey.} other {# surveys.}}',
    description:
      '[Type: paragraph][Vis.: med] - Testing plurals, showing number of surveys'
  },
  MULTI_C: {
    id: 'IntlKitchenSink.multiC',
    defaultMessage:
      'This is your {count, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} survey! ',
    description:
      '[Type: paragraph][Vis.: med] - Testing ordinals, showing which number survey this is'
  },
  NUM_A: {
    id: 'IntlKitchenSink.numA',
    defaultMessage: 'The price of this package is {price, number, EUR}',
    description:
      '[Type: paragraph][Vis.: med] - Testing currencies, showing EUR price'
  },
  NUM_B: {
    id: 'IntlKitchenSink.numB',
    defaultMessage:
      'You have completed {percentage, number, percent} of all your surveys',
    description:
      '[Type: paragraph][Vis.: med] - Testing currencies, showing USD price'
  },
  NUM_C: {
    id: 'IntlKitchenSink.numC',
    defaultMessage: 'Your account balance shows: {accountBalance}',
    description:
      '[Type: paragraph][Vis.: med] - Testing currencies, showing prices in various currencies'
  }
});

const CodeExample = props => (
  <Card
    title={<Typography variant="cardTitle">{props.title}</Typography>}
    className="wds-m-b-5"
  >
    <Grid fluid noBleeds>
      <Row>
        <Col>
          <Pane padding>
            <code>{props.msg}</code>
          </Pane>
        </Col>
        <Col>
          <Pane padding>{props.children}</Pane>
        </Col>
      </Row>
    </Grid>
  </Card>
);

CodeExample.propTypes = {
  title: PropTypes.string.isRequired,
  msg: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

const intlFxn = value => {
  if (value === 'utils') {
    return (
      <div>
        {t(
          MSG.MESSAGE_C,
          {
            firstName: <b key="john3">John</b>,
            lastName: <em key="doe3">Doe</em>
          },
          { html: false }
        )}
      </div>
    );
  }
  return null;
};

const TwoHoursBefore = new Date();
TwoHoursBefore.setHours(TwoHoursBefore.getHours() - 2);

const IntlKitchenSink = () => (
  <Grid>
    <Typography variant="hero2">Intl Kitchensink</Typography>

    <Typography component="section" variant="sectionTitle">
      Formatted Messages (Components)
    </Typography>
    <CodeExample msg={MSG.MESSAGE_A.defaultMessage} title="Simple string">
      <T desc={MSG.MESSAGE_A} />
    </CodeExample>
    <CodeExample
      msg={MSG.MESSAGE_B.defaultMessage}
      title="Single token substitution"
    >
      <T desc={MSG.MESSAGE_B} values={{ packageName: 'Ultimate' }} />
    </CodeExample>
    <CodeExample
      msg={MSG.MESSAGE_B.defaultMessage}
      title="Single token substitution using function"
    >
      {t(MSG.MESSAGE_B, { packageName: 'Ultimate' }, { html: false })}
    </CodeExample>
    <CodeExample
      msg={MSG.MESSAGE_C.defaultMessage}
      title="Tokens substitution with react components"
    >
      <T
        desc={MSG.MESSAGE_C}
        values={{
          firstName: <b key="john">John</b>,
          lastName: <em key="doe">Doe</em>
        }}
      />
    </CodeExample>
    <CodeExample
      msg={MSG.MESSAGE_C.defaultMessage}
      title="React component substitutions using function"
    >
      {intlFxn('utils')}
    </CodeExample>
    <CodeExample
      msg={MSG.MESSAGE_C.defaultMessage}
      title="Tokens substitution with react components in popout"
    >
      <div>
        <Popout
          placement="right"
          trigger={<Button>Click for popout</Button>}
          clickable
        >
          <PopoutBody>
            <Typography component="p" variant="body">
              <T
                desc={MSG.MESSAGE_C}
                values={{
                  firstName: <b key="john2">John</b>,
                  lastName: <em key="doe2">Doe</em>
                }}
              />
            </Typography>
          </PopoutBody>
        </Popout>
      </div>
    </CodeExample>

    <Typography component="section" variant="sectionTitle">
      Formatted Messages (HTML replacement)
    </Typography>
    <CodeExample
      msg={MSG.HTML_A.defaultMessage}
      title="Translation with HTML content"
    >
      <T desc={MSG.HTML_A} html />
    </CodeExample>
    <CodeExample
      msg={MSG.HTML_A.defaultMessage}
      title="string with html using function"
    >
      {t(MSG.HTML_A, '', { html: true })}
    </CodeExample>

    <Typography component="section" variant="sectionTitle">
      Formatted Messages (Plurals & Ordinals)
    </Typography>

    <CodeExample title="selects" msg={MSG.MULTI_A.defaultMessage}>
      <T desc={MSG.MULTI_A} values={{ gender: 'female' }} />
    </CodeExample>
    <CodeExample title="plurals" msg={MSG.MULTI_B.defaultMessage}>
      <T desc={MSG.MULTI_B} values={{ num: 3 }} />
    </CodeExample>
    <CodeExample title="ordinals" msg={MSG.MULTI_C.defaultMessage}>
      <T desc={MSG.MULTI_C} values={{ count: 1 }} />
    </CodeExample>

    <Typography component="section" variant="sectionTitle">
      Formatted Numbers
    </Typography>
    <CodeExample msg={MSG.NUM_A.defaultMessage} title="Static currency">
      <T desc={MSG.NUM_A} values={{ price: 876543.21 }} />
    </CodeExample>
    <CodeExample msg={MSG.NUM_B.defaultMessage} title="Percentage">
      <T desc={MSG.NUM_B} values={{ percentage: 0.1 }} />
    </CodeExample>
    <CodeExample msg={MSG.MULTI_C.defaultMessage} title="Big number">
      <T desc={MSG.MULTI_C} values={{ count: 1000000 }} />
    </CodeExample>
    <CodeExample msg={MSG.NUM_C.defaultMessage} title="Various currencies">
      <T
        desc={MSG.NUM_C}
        values={{
          accountBalance: (
            <FormattedNumber
              value={12345678}
              formatStyle="currency"
              currency="CAD"
            />
          )
        }}
      />
    </CodeExample>
    <CodeExample msg={MSG.NUM_C.defaultMessage} title="Various currencies">
      <T
        desc={MSG.NUM_C}
        values={{
          accountBalance: (
            <FormattedNumber
              value={12345678}
              formatStyle="currency"
              currency="JPY"
            />
          )
        }}
      />
    </CodeExample>

    <Typography component="section" variant="sectionTitle">
      Formatted Dates
    </Typography>
    <CodeExample
      msg={MSG.DATE_A.defaultMessage}
      title="Date substitution (short)"
    >
      <T desc={MSG.DATE_A} values={{ expirationDate: new Date() }} />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_B.defaultMessage}
      title="Date substitution (long)"
    >
      <T desc={MSG.DATE_B} values={{ dueDate: new Date() }} />
    </CodeExample>
    <CodeExample
      msg={MSG.TIME_A.defaultMessage}
      title="Time substitution (short)"
    >
      <T desc={MSG.TIME_A} values={{ expiryTime: new Date() }} />
    </CodeExample>
    <CodeExample
      msg={MSG.TIME_B.defaultMessage}
      title="Time substitution (long)"
    >
      <T desc={MSG.TIME_B} values={{ expiryTime: new Date() }} />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_B.defaultMessage}
      title="Datetime substitution (short)"
    >
      <T desc={MSG.DATE_B} values={{ dueDate: new Date() }} />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_B.defaultMessage}
      title="Datetime substitution (long)"
    >
      <T desc={MSG.DATE_B} values={{ dueDate: new Date() }} />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_C.defaultMessage}
      title="Datetime substitution with <FormattedDateTime>"
    >
      <T
        desc={MSG.DATE_C}
        values={{
          formattedDate: (
            <FormattedDateTime
              value={new Date()}
              weekday="long"
              day="numeric"
              month="long"
              year="numeric"
            />
          )
        }}
      />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_C.defaultMessage}
      title="Datetime substitution with <FormattedDateTime>"
    >
      <T
        desc={MSG.DATE_C}
        values={{
          formattedDate: (
            <FormattedDateTime
              value={new Date()}
              hour="2-digit"
              minute="2-digit"
            />
          )
        }}
      />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_C.defaultMessage}
      title="Relative time substitution with <FormattedRelativeTime>"
    >
      <T
        desc={MSG.DATE_C}
        values={{
          formattedDate: <FormattedRelativeTime value={TwoHoursBefore} />
        }}
      />
    </CodeExample>
    <CodeExample
      msg={MSG.DATE_C.defaultMessage}
      title="Relative time substitution with <FormattedRelativeTime>"
    >
      <T
        desc={MSG.DATE_C}
        values={{
          formattedDate: <FormattedRelativeTime value={TwoHoursBefore} />
        }}
      />
    </CodeExample>
  </Grid>
);

export default IntlKitchenSink;
